import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { auth } from '../firebase';
import toast from 'react-hot-toast';

interface AddActivityModalProps {
  leadId: string;
  leadName: string;
  onClose: () => void;
  onSaved: () => void;
}

const activityTypes = [
  { value: 'call', label: 'שיחה' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'meeting', label: 'פגישה' },
  { value: 'property_sent', label: 'שליחת נכס' },
  { value: 'note', label: 'הערה' },
];

const contactTypes = ['call', 'whatsapp', 'meeting'];

export default function AddActivityModal({ leadId, leadName, onClose, onSaved }: AddActivityModalProps) {
  const [activityType, setActivityType] = useState('call');
  const [description, setDescription] = useState('');
  const [updateLastContact, setUpdateLastContact] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleTypeChange = (type: string) => {
    setActivityType(type);
    setUpdateLastContact(contactTypes.includes(type));
  };

  const handleSave = async () => {
    if (!activityType) {
      toast.error('בחר סוג פעילות');
      return;
    }

    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const idToken = await user.getIdToken();

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/add-activity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId,
          activityType,
          description: description.trim(),
          updateLastContact,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save activity');
      }

      toast.success('פעילות נשמרה');
      onSaved();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]" dir="rtl">
      <div className="bg-gray-800 rounded-xl w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-lg font-bold text-white">הוסף פעילות – {leadName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">סוג פעילות</label>
            <div className="grid grid-cols-3 gap-2">
              {activityTypes.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => handleTypeChange(t.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activityType === t.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">תיאור פעילות</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 h-24 resize-none"
              placeholder="מה קרה? סיכום קצר..."
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={updateLastContact}
              onChange={(e) => setUpdateLastContact(e.target.checked)}
              className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-300">עדכן קשר אחרון</span>
          </label>
        </div>

        <div className="p-5 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors text-sm"
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors text-sm"
          >
            <Save size={15} />
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </div>
    </div>
  );
}
